import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AlertCircle } from 'lucide-react';
import type { UIMessage } from '@/types/chat';
import { ToolCallCard } from './ToolCallCard';
import { DiffPreview } from './DiffPreview';
import { ClarificationCard } from './ClarificationCard';
import { FeedbackActions } from './FeedbackActions';
import { SuggestionChips } from './SuggestionChips';
import { FormulaPill } from './FormulaPill';
import { useChatStore } from '@/stores/chatStore';
import { shouldHideAssistantBubble, stripBoilerplate } from '@/utils/cleanText';

interface Props {
  m: UIMessage;
  conversationId: string | null;
  isLast?: boolean;
  onClarifyPick?: (interpretation: string) => void;
  onSuggestionPick?: (text: string) => void;
}

export function MessageBubble({ m, conversationId, isLast, onClarifyPick, onSuggestionPick }: Props) {
  const isUser = m.role === 'user';
  const { dismissClarify } = useChatStore();

  const hasTools = !isUser && (m.tools?.length ?? 0) > 0;
  const hasDiff = !isUser && m.diff && m.diff.changes.length > 0;
  const hasClarify = !isUser && m.clarify;

  // Strip LLM boilerplate from assistant messages (preambles like "I'll
  // sort the data..." and postambles like "Done — 10 rows added"). The
  // diff card already shows what changed; repeating it in prose is pure
  // attention tax. User messages stay verbatim.
  const displayContent = isUser ? m.content : stripBoilerplate(m.content);
  // Hide the bubble entirely when:
  //   (a) cleaned text is empty AND diff/tool cards carry the meaning, OR
  //   (b) streaming AND no content yet (LoadingPulse already shows progress —
  //       rendering an empty "…" bubble alongside it = double indicator).
  const hideEmptyBubble =
    !isUser
    && !m.error
    && (
      (m.streaming && !displayContent)
      || (!m.streaming && shouldHideAssistantBubble(displayContent, hasDiff || hasTools))
    );

  return (
    <div className={['flex flex-col gap-1.5', isUser ? 'items-end' : 'items-start'].join(' ')}>
      {hasTools && (
        <div className="w-full max-w-[90%] space-y-1">
          {m.tools!.map((t) => (
            <ToolCallCard key={`${t.id}-${t.iteration}`} tool={t} />
          ))}
        </div>
      )}

      {hasClarify && (
        <ClarificationCard
          messageId={m.id}
          state={m.clarify!}
          onPick={(interp) => onClarifyPick?.(interp)}
          onDismiss={() => dismissClarify(m.id)}
          disabled={m.clarify!.status !== 'pending'}
        />
      )}

      {hasDiff && <DiffPreview conversationId={conversationId} message={m} />}

      {(displayContent || m.streaming || m.error) && !hideEmptyBubble && (
        <div
          className={[
            'max-w-[90%] rounded-lg px-3 py-2 text-sm leading-relaxed',
            isUser
              ? 'bg-primary text-white'
              : 'bg-surface text-text-primary dark:bg-surface-dark dark:text-text-primary-dark',
          ].join(' ')}
        >
          {m.error ? (
            <div className="flex items-start gap-2 text-red-500">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{m.error}</span>
            </div>
          ) : isUser ? (
            <p className="whitespace-pre-wrap">{m.content}</p>
          ) : (
            <div className="prose-sm max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code({ inline, className, children, ...props }: React.HTMLAttributes<HTMLElement> & { inline?: boolean }) {
                    // Day 3: inline Excel formulas get the FormulaPill
                    // treatment — hover for arg breakdown. Non-formula
                    // code spans keep their plain-mono styling.
                    const text = String(children ?? '').trim();
                    if (inline && text.startsWith('=') && text.length > 3) {
                      return <FormulaPill formula={text} />;
                    }
                    return inline ? (
                      <code
                        className="rounded bg-black/10 px-1 py-0.5 font-mono text-xs dark:bg-white/10"
                        {...props}
                      >
                        {children}
                      </code>
                    ) : (
                      <pre className="my-2 overflow-x-auto rounded-md bg-black/5 p-2 font-mono text-xs dark:bg-white/10">
                        <code className={className}>{children}</code>
                      </pre>
                    );
                  },
                  a({ href, children, ...props }) {
                    return (
                      <a href={href} target="_blank" rel="noreferrer" className="text-primary underline" {...props}>
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {displayContent || (m.streaming ? '…' : '')}
              </ReactMarkdown>
            </div>
          )}

          {m.usage && !m.streaming && (() => {
            const total = m.usage.tokens_input + m.usage.tokens_output;
            const display = total >= 1000 ? `${(total / 1000).toFixed(1)}K` : `${total}`;
            return (
              <div
                className="mt-1 text-[10px] text-black/40 dark:text-white/40"
                title={`${total.toLocaleString()} tokens this turn (input + output)`}
              >
                {display}
              </div>
            );
          })()}
        </div>
      )}

      {!isUser && !m.streaming && !m.error && m.content && (
        <div className="relative">
          <FeedbackActions
            conversationId={conversationId}
            messageId={m.id}
            aiOutput={m.content}
          />
        </div>
      )}

      {/* Proactive follow-up chips — last assistant turn only. Older
          turns keep their chips in state but we don't re-render them,
          otherwise the thread would fill with stale suggestions. */}
      {!isUser && isLast && !m.streaming && !m.error && (m.suggestions?.length ?? 0) > 0 && (
        <SuggestionChips
          suggestions={m.suggestions!}
          onPick={(text) => onSuggestionPick?.(text)}
        />
      )}
    </div>
  );
}
